import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Avatar,
  Badge
} from '@mui/material'
import {
  Refresh,
  ExitToApp,
  Computer,
  LocationOn,
  AccessTime,
  Person
} from '@mui/icons-material'
import axios from 'axios'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

const WhoisOnline = () => {
  const [sesiones, setSesiones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    loadSesiones()
    // Auto-refresh cada 30 segundos
    const interval = setInterval(loadSesiones, 30000)
    return () => clearInterval(interval)
  }, [])

  const loadSesiones = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/auth/sesiones/activas', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSesiones(response.data)
      setError(null)
    } catch (error) {
      setError('Error al cargar sesiones activas: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleCerrarSesion = async (sesionId, usuarioNombre) => {
    if (!window.confirm(`¿Está seguro de cerrar la sesión de ${usuarioNombre}?`)) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/auth/sesiones/${sesionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess(`Sesión de ${usuarioNombre} cerrada exitosamente`)
      loadSesiones()
      setTimeout(() => setSuccess(null), 3000)
    } catch (error) {
      setError('Error al cerrar sesión: ' + (error.response?.data?.detail || error.message))
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Desconocido'
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm:ss", { locale: es })
    } catch {
      return dateString
    }
  }

  const getTimeAgo = (dateString) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMinutes = Math.floor((now - date) / 1000 / 60)

      if (diffMinutes < 1) return 'Ahora mismo'
      if (diffMinutes === 1) return 'Hace 1 minuto'
      if (diffMinutes < 60) return `Hace ${diffMinutes} minutos`

      const diffHours = Math.floor(diffMinutes / 60)
      if (diffHours === 1) return 'Hace 1 hora'
      if (diffHours < 24) return `Hace ${diffHours} horas`

      const diffDays = Math.floor(diffHours / 24)
      if (diffDays === 1) return 'Hace 1 día'
      return `Hace ${diffDays} días`
    } catch {
      return ''
    }
  }

  const getBrowserInfo = (userAgent) => {
    if (!userAgent) return 'Desconocido'
    if (userAgent.includes('Chrome')) return 'Chrome'
    if (userAgent.includes('Firefox')) return 'Firefox'
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari'
    if (userAgent.includes('Edge')) return 'Edge'
    return 'Otro navegador'
  }

  const getRolColor = (rol) => {
    switch (rol) {
      case 'administrador': return 'error'
      case 'rector': return 'secondary'
      case 'editor': return 'warning'
      case 'consultor': return 'info'
      default: return 'default'
    }
  }

  const getRolLabel = (rol) => {
    switch (rol) {
      case 'administrador': return 'Admin'
      case 'rector': return 'Rector'
      case 'editor': return 'Editor'
      case 'consultor': return 'Consultor'
      default: return rol
    }
  }

  // Agrupar sesiones por usuario
  const usuariosConectados = sesiones.reduce((acc, sesion) => {
    const userId = sesion.usuario_id
    if (!acc[userId]) {
      acc[userId] = {
        usuario_id: userId,
        nombre: sesion.nombre,
        email: sesion.email,
        rol: sesion.rol,
        sesiones: []
      }
    }
    acc[userId].sesiones.push(sesion)
    return acc
  }, {})

  const usuariosArray = Object.values(usuariosConectados)

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Person sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" component="h2">
              Usuarios Conectados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {usuariosArray.length} usuario{usuariosArray.length !== 1 ? 's' : ''} en línea ({sesiones.length} sesión{sesiones.length !== 1 ? 'es' : ''} activa{sesiones.length !== 1 ? 's' : ''})
            </Typography>
          </Box>
        </Box>
        <Tooltip title={loading ? "" : "Actualizar"}>
          <span>
            <IconButton onClick={loadSesiones} disabled={loading}>
              <Refresh />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading && sesiones.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : usuariosArray.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No hay usuarios conectados actualmente
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Dispositivo/Navegador</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Última Actividad</TableCell>
                <TableCell>Conectado Desde</TableCell>
                <TableCell align="center">Sesiones</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usuariosArray.map((usuario) => {
                const sesionMasReciente = usuario.sesiones.sort((a, b) =>
                  new Date(b.last_activity) - new Date(a.last_activity)
                )[0]

                return (
                  <TableRow key={usuario.usuario_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Badge
                          color="success"
                          variant="dot"
                          overlap="circular"
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                        >
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                            {usuario.nombre?.charAt(0)?.toUpperCase() || 'U'}
                          </Avatar>
                        </Badge>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {usuario.nombre}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {usuario.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getRolLabel(usuario.rol)}
                        color={getRolColor(usuario.rol)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Computer fontSize="small" color="action" />
                        <Typography variant="body2">
                          {getBrowserInfo(sesionMasReciente.user_agent)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="body2">
                          {sesionMasReciente.ip_address || 'Desconocida'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {getTimeAgo(sesionMasReciente.last_activity)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(sesionMasReciente.last_activity)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(sesionMasReciente.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={usuario.sesiones.length}
                        size="small"
                        color={usuario.sesiones.length > 1 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {usuario.sesiones.length === 1 ? (
                        <Tooltip title="Cerrar sesión">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCerrarSesion(usuario.sesiones[0].id, usuario.nombre)}
                          >
                            <ExitToApp fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {usuario.sesiones.map((sesion, idx) => (
                            <Tooltip key={sesion.id} title={`Cerrar sesión ${idx + 1}`}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCerrarSesion(sesion.id, usuario.nombre)}
                              >
                                <ExitToApp fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccessTime fontSize="small" color="action" />
        <Typography variant="caption" color="text.secondary">
          Actualización automática cada 30 segundos
        </Typography>
      </Box>
    </Box>
  )
}

export default WhoisOnline
